import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pagination, Spin, notification } from "antd";

import { ReactComponent as VodActionsIcon } from "../../../../../../assets/icons/ThreeDotsVertical.svg";

import { fetchVodsStart } from "../../../../../../store/features/vods/vods-slice";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import {
  IMAGE_BASE_URL,
  CARDS_PAGE_SIZE,
  VIDEO_BASE_URL,
} from "../../../../../../common/config";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { SortType } from "../../../../../../interfaces/screens/fetch-params-types";

import { NotFoundText } from "../../../../../../components/common/styles";
import DeleteModal from "../../../../../../components/common/DeleteModal";
import { deleteVodStart } from "../../../../../../store/features/vod/vod-slice";
import {
  StyledCardTableLayout,
  StyledCardMetaWrapper,
  StyledCard,
  StyledCardImage,
  StyledCardMeta,
  CardActionsButton,
} from "../../../../../../components/common/styles/tableStyles";
import { TableHeader } from "../../../../../../components/table/TableHeader";
import { IVod } from "../../../../../../interfaces/store/vod-interface";
import { IFetchParams } from "../../../../../../interfaces/base";

export const TechniqueVideosTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { id: trainerId } = useParams<{ id: string }>();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [currentVodId, setCurrentVodId] = useState<string>("");
  const [deleteConfirmLoading, setDeleteConfirmLoading] =
    useState<boolean>(false);

  const { vods, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.vods
  );
  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: deleteError,
    operation: deleteOperation,
    requestState: deleteRequestState,
  } = useAppSelector((state) => state.vod);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 }
        : pagination;

    fetchVods({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || deleteError) {
      openNotification();
    }
  }, [error || deleteError]);

  useEffect(() => {
    if (!openDeleteModal) {
      setCurrentVodId("");
    }
  }, [openDeleteModal]);

  useEffect(() => {
    if (deleteRequestState === "finish" && deleteOperation === "delete") {
      setDeleteConfirmLoading(false);
      setOpenDeleteModal(false);
      fetchVods({});
    }
  }, [deleteRequestState, deleteOperation]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: CARDS_PAGE_SIZE * (page - 1),
    };

    fetchVods({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchVods({
      sort: value,
      pagination: { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 },
    });
  };

  const handleDelete = () => {
    setDeleteConfirmLoading(true);
    dispatch(deleteVodStart({ vodId: currentVodId }));
  };

  const handleCreateNewVod = () => {
    navigate(`/trainers/${trainerId}/technique-videos/add`);
  };

  const imgSrc = (vod: IVod) => {
    const videoName = vod.video?.replace(/\.mp4$/, "") || "";

    return vod.image
      ? `${IMAGE_BASE_URL}/${vod.image}`
      : videoName
      ? `${VIDEO_BASE_URL}/${videoName}__thumb_regular.jpg`
      : undefined;
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const fetchVods = (body: IFetchParams) => {
    dispatch(
      fetchVodsStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
        filter: { trainer: trainerId, workout: null },
      })
    );
  };

  return (
    <>
      {contextHolder}
      <TableHeader
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
        handleCreateNew={handleCreateNewVod}
        createNewText="Create New Video"
        currentSortBy={sort?.sortBy || ""}
      />
      <Spin spinning={loading}>
        {vods.length === 0 && !loading && (
          <NotFoundText>{t("No Videos found")}</NotFoundText>
        )}
        <StyledCardTableLayout>
          {vods.map((vod) => (
            <StyledCard
              key={vod._id}
              cover={<StyledCardImage src={imgSrc(vod)} />}
            >
              <CardActionsButton
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: t("Edit"),
                      onClick: () =>
                        navigate(
                          `/trainers/${trainerId}/technique-videos/${vod._id}`,
                          {
                            state: { isEdit: true },
                          }
                        ),
                    },
                    {
                      key: "delete",
                      label: t("Delete"),
                      onClick: () => {
                        setCurrentVodId(vod._id);
                        setOpenDeleteModal(true);
                      },
                    },
                  ],
                }}
              >
                <VodActionsIcon />
              </CardActionsButton>
              <StyledCardMetaWrapper
                onClick={() =>
                  navigate(`/trainers/${trainerId}/technique-videos/${vod._id}`)
                }
              >
                <StyledCardMeta
                  title={vod.name}
                  description={vod.duration ? formatDuration(vod.duration) : ""}
                />
              </StyledCardMetaWrapper>
            </StyledCard>
          ))}
        </StyledCardTableLayout>
        <Pagination
          current={metadata?.page}
          pageSize={CARDS_PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={deleteConfirmLoading}
        />
      </Spin>
    </>
  );
};
